import React, { useState, useEffect, useMemo } from "react";
import {
  Table, Card, Select, DatePicker, Button, Tag, Empty, Spin, Pagination, Grid, message,
} from "antd";
import {
  StarFilled, ReloadOutlined, ArrowRightOutlined, DownloadOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { useCenterServices } from "../../../hooks/useCenterServices";
import { useServicesStats, fetchServicesStats as getServicesStats } from "../../../hooks/useServiceAnalysis";
const { useBreakpoint } = Grid;

// Rows fetched per request while exporting (lower this if your backend caps PageSize).
const EXPORT_PAGE_SIZE = 100;

const money = (n) =>
  `${Number(n ?? 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} EGP`;
const num = (n) => Number(n ?? 0).toLocaleString("en-US");
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

const useDebounced = (value, delay = 400) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

const StatCard = ({ label, value, sub }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-1 md:p-2 min-w-0">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-lg md:text-xl font-semibold text-primary mt-1 break-words">{value}</div>
    {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

const PriceCell = ({ record }) => {
  const finalPrice = record.priceAfterDiscount ?? record.price;

  // No price at all -> show a dash instead of "0 EGP"
  if (finalPrice == null) {
    return <div className="font-medium text-gray-400">-</div>;
  }

  return (
    <div className="leading-tight">
      <div className="font-medium">{money(finalPrice)}</div>
      {record.price != null && record.offerPercentage > 0 && (
        <div className="text-xs text-gray-400">
          <span className="line-through">{money(record.price)}</span>
          <span className="ml-1 text-green-600">-{record.offerPercentage}%</span>
        </div>
      )}
    </div>
  );
};

const StatusTag = ({ active }) => (
  <Tag color={active ? "green" : "default"}>{active ? "Active" : "Inactive"}</Tag>
);

function ServicesAnalysis() {
  const screens = useBreakpoint();
  const isMobile = screens.md === false;

  const [search] = useState("");
  const [serviceId, setServiceId] = useState(undefined); // selected service filter
  const [isActive, setIsActive] = useState(undefined);
  const [onlyFavorites] = useState(false);
  const [from, setFrom] = useState(null); // dayjs | null -> server defaults to current month
  const [to, setTo] = useState(null); // dayjs | null -> server defaults to current month
  const [sortBy] = useState("usageCount");
  const [sortDesc] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [exporting, setExporting] = useState(false);

  const debouncedSearch = useDebounced(search);

  // Services list for the dropdown (GetMyServices)
  const { services: myServices, isLoading: servicesLoading } = useCenterServices();

  const serviceOptions = useMemo(
    () =>
      (myServices ?? []).map((s) => ({
        value: s.id,
        // title + Arabic title, so duplicates like "Pakcage3" are distinguishable
        label:
          s.titleAr && s.titleAr.trim() !== s.title?.trim()
            ? `${s.title} — ${s.titleAr}`
            : s.title,
        search: `${s.titleEn ?? ""} ${s.titleAr ?? ""} ${s.title ?? ""}`.toLowerCase(),
      })),
    [myServices]
  );

  // Any filter change goes back to page 1.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, isActive, onlyFavorites, from, to, sortBy, sortDesc, serviceId]);

  const params = useMemo(
    () => ({
      ServiceId: serviceId || undefined,
      SearchTerm: debouncedSearch.trim() || undefined,
      IsActive: isActive,
      IsFavorite: onlyFavorites ? true : undefined,
      From: from?.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      To: to?.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      PageNumber: page,
      PageSize: pageSize,
      SortBy: sortBy,
      SortDescending: sortDesc,
    }),
    [serviceId, debouncedSearch, isActive, onlyFavorites, from, to, page, pageSize, sortBy, sortDesc]
  );

  const { data, isPending, isFetching, isError, error, refetch } = useServicesStats(params);

  useEffect(() => {
    if (isError) {
      if (error?.response?.status === 403) window.location.href = "/sub";
      else message.error("Failed to load services analysis");
    }
  }, [isError, error]);

  const services = data?.services ?? [];
  const summary = data?.summary;
  const total = data?.totalCount ?? 0;

  // Sorting is handled by the dropdowns above, the table only paginates.
  const handleTableChange = (pag) => {
    setPage(pag.current);
    setPageSize(pag.pageSize);
  };

  // ---------- Export to Excel ----------
  const handleExport = async () => {
    setExporting(true);
    try {
      // Fetch ALL rows matching the current filters, page by page.
      const all = [];
      let lastSummary = summary;
      for (let p = 1; all.length < total; p++) {
        const res = await getServicesStats({
          ...params,
          PageNumber: p,
          PageSize: EXPORT_PAGE_SIZE,
        });
        const batch = res?.services ?? [];
        if (res?.summary) lastSummary = res.summary;
        if (batch.length === 0) break;
        all.push(...batch);
      }

      if (all.length === 0) {
        message.info("Nothing to export");
        return;
      }

      const rows = all.map((r, i) => ({
        "#": i + 1,
        "Service": r.title,
        "Service (Arabic)": r.titleAr ?? "",
        "Price": r.price ?? "",
        "Offer %": r.offerPercentage ?? 0,
        "Price After Discount": r.priceAfterDiscount ?? r.price ?? "",
        "Total Usage": r.usageCount ?? 0,
        "Paid Usage": r.paidUsageCount ?? 0,
        "Package Usage": r.packageUsageCount ?? 0,
        "Patients": r.uniquePatientsCount ?? 0,
        "Status": r.isActive ? "Active" : "Inactive",
        "Favorite": r.isFavorite ? "Yes" : "No",
        "Last Used": r.lastUsedAt ? new Date(r.lastUsedAt).toLocaleDateString("en-GB") : "",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      // Column widths based on the longest value in each column
      ws["!cols"] = Object.keys(rows[0]).map((k) => ({
        wch: Math.min(
          40,
          Math.max(k.length, ...rows.map((row) => String(row[k] ?? "").length)) + 2
        ),
      }));

      const s = lastSummary ?? {};
      const summaryWs = XLSX.utils.aoa_to_sheet([
        ["From", from ? from.format("YYYY-MM-DD") : data?.from?.slice(0, 10) ?? ""],
        ["To", to ? to.format("YYYY-MM-DD") : data?.to?.slice(0, 10) ?? ""],
        [],
        ["Services", s.servicesCount ?? 0],
        ["Total usage", s.totalUsageCount ?? 0],
        ["Paid usage", s.totalPaidUsageCount ?? 0],
        ["Package usage", s.totalPackageUsageCount ?? 0],
        ["Covered by packages (EGP)", s.totalPackageCoveredAmount ?? 0],
      ]);
      summaryWs["!cols"] = [{ wch: 28 }, { wch: 16 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Services");
      XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `services-analysis-${stamp}.xlsx`);
      message.success("Export complete");
    } catch (e) {
      message.error("Failed to export");
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 56,
      render: (_, __, i) => (page - 1) * pageSize + i + 1,
    },
    {
      title: "Service",
      dataIndex: "title",
      key: "title",
      render: (_, r) => (
        <div className="leading-tight min-w-[180px]">
          <div className="font-medium flex items-center gap-1">
            {r.isFavorite && <StarFilled className="text-yellow-500" />}
            {r.title}
          </div>
          {r.titleAr && (
            <div className="text-xs text-gray-400" dir="rtl">
              {r.titleAr}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (_, r) => <PriceCell record={r} />,
    },
    {
      title: "Usage",
      dataIndex: "usageCount",
      key: "usageCount",
      render: (v, r) => (
        <div className="leading-tight">
          <div className="font-medium">{num(v)}</div>
          <div className="text-xs text-gray-400">
            {num(r.paidUsageCount)} paid · {num(r.packageUsageCount)} package
          </div>
        </div>
      ),
    },
    {
      title: "Patients",
      dataIndex: "uniquePatientsCount",
      key: "uniquePatientsCount",
      render: num,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (v) => <StatusTag active={v} />,
    },
  ];

  const renderMobileCards = () => (
    <Spin spinning={isFetching}>
      {!isPending && services.length === 0 ? (
        <Empty className="py-10" description="No services match these filters" />
      ) : (
        <div className="flex flex-col gap-3 min-h-[120px]">
          {services.map((r) => (
            <div key={r.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-primary to-primary/50" />
              <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-bold text-primary text-base leading-tight break-words flex items-center gap-1">
                      {r.isFavorite && <StarFilled className="text-yellow-500" />}
                      {r.title}
                    </h3>
                    {r.titleAr && (
                      <div className="text-xs text-gray-400" dir="rtl">
                        {r.titleAr}
                      </div>
                    )}
                  </div>
                  <StatusTag active={r.isActive} />
                </div>

                <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                  <div>
                    <div className="text-xs text-gray-400">Price</div>
                    <PriceCell record={r} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Usage</div>
                    <div className="font-medium">{num(r.usageCount)}</div>
                    <div className="text-xs text-gray-400">
                      {num(r.paidUsageCount)} paid · {num(r.packageUsageCount)} package
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Patients</div>
                    <div className="font-medium">{num(r.uniquePatientsCount)}</div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
                  Last used {fmtDate(r.lastUsedAt)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {total > pageSize && (
        <div className="flex justify-center mt-4">
          <Pagination
            simple
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger={false}
            onChange={(p) => {
              setPage(p);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        </div>
      )}
    </Spin>
  );

  return (
    <div className="p-0 sm:p-4 md:p-6 overflow-x-hidden">
      {/* Date range (div/span instead of nested <p>, which is invalid HTML) */}
      {data?.from && data?.to && (
        <div className="text-gray-600 flex items-center text-sm pb-1 gap-3">
          <span>For Time From:</span>
          <span className="text-sm text-gray-500 font-light">
            {new Date(`${data.from}Z`).toDateString()}
          </span>
          <ArrowRightOutlined />
          <span className="text-sm text-gray-500 font-light">
            {new Date(`${data.to}Z`).toLocaleDateString()}
          </span>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
        <StatCard label="Services" value={num(summary?.servicesCount)} />
        <StatCard label="Total usage" value={num(summary?.totalUsageCount)} />
        <StatCard
          label="Paid usage"
          value={num(summary?.totalPaidUsageCount)}
          sub={`${num(summary?.totalPackageUsageCount)} from packages`}
        />
        <StatCard label="Covered by packages" value={money(summary?.totalPackageCoveredAmount)} />
      </div>

      {/* Filters */}
      <Card className="mb-4" styles={{ body: { padding: isMobile ? 2 : 16 } }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
          <Select
            className="w-full"
            showSearch
            allowClear
            placeholder="Select service"
            loading={servicesLoading}
            value={serviceId}
            onChange={setServiceId}
            options={serviceOptions}
            filterOption={(input, option) => option.search.includes(input.toLowerCase())}
          />
          <DatePicker
            className="w-full"
            value={from}
            onChange={setFrom}
            allowClear
            placeholder="From"
            disabledDate={(current) => !!to && !!current && current.isAfter(to, "day")}
          />
          <DatePicker
            className="w-full"
            value={to}
            onChange={setTo}
            allowClear
            placeholder="To"
            disabledDate={(current) => !!from && !!current && current.isBefore(from, "day")}
          />
          <Select
            className="w-full"
            placeholder="Status"
            allowClear
            value={isActive}
            onChange={setIsActive}
            options={[
              { value: true, label: "Active" },
              { value: false, label: "Inactive" },
            ]}
          />
          <div className="flex gap-2">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isFetching && !isPending}
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exporting}
              disabled={isPending || total === 0}
              style={{ background: "#298E9F" }}
            >
              Export
            </Button>
          </div>
        </div>
      </Card>

      {isMobile ? (
        renderMobileCards()
      ) : (
        <Table
          dataSource={services}
          columns={columns}
          rowKey="id"
          loading={isFetching}
          scroll={{ x: 1000 }}
          locale={{ emptyText: <Empty description="No services match these filters" /> }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t, r) => `${r[0]}-${r[1]} of ${t} services`,
          }}
          onChange={handleTableChange}
        />
      )}
    </div>
  );
}

export default ServicesAnalysis;